/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BuilderUserContext } from '@gh/builder-shared';
import { DateTimeTypes, FormState, FormStateBuilder, getDialogInitData } from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  FormMode,
  getControl,
  NotificationService,
  WaitingService,
} from '@gh/core-ui';
import { currentDate } from '@gh/core-util';
import {
  fromForm,
  TodoDetails,
  TodoForm,
  TodoListBusinessService,
  TodoInputDialogConfig,
} from '../../../resources';
import { UserType } from '@gh/builder-shared-data';

@Component({
  standalone: false,
  selector: 'gh-mutate-todo-dialog',
  templateUrl: 'mutate-todo-dialog.component.html',
  styleUrls: ['mutate-todo-dialog.component.scss'],
})
export class MutateTodoDialogComponent implements DialogInput<TodoInputDialogConfig>, OnInit {
  input: TodoInputDialogConfig;

  context = {
    BASE_URL: this.builderUserContext.baseUrl,
  };

  mutateForm: FormState<TodoForm>;

  get isMasterOrCorporate(): boolean {
    return this.builderUserContext.userType === UserType.Corporate || this.builderUserContext.userType === UserType.Master;
  }

  constructor(private dialogRef: MatDialogRef<MutateTodoDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private formStateBuilder: FormStateBuilder,
              private todoListBusiness: TodoListBusinessService,
              private builderUserContext: BuilderUserContext,
              private notificationService: NotificationService,
              private waitingService: WaitingService) {
  }

  ngOnInit(): void {
    const todo = getDialogInitData(this.dialogData);
    const { id, firstName, lastName } = this.builderUserContext.data;
    const fullName = this.isMasterOrCorporate ? '' : `${firstName} ${lastName}`;
    const initFormValue =
      (this.input.todoId)
      ? todo
      : { ...todo, assignee: { fullName, userId: id } };
    this.mutateForm = this.formStateBuilder.buildPlain<TodoForm>({
      value: {
        ...initFormValue,
        dueDate: this.input.todoId ? initFormValue.dueDate : currentDate(),
      },
      mode: this.input.activityTodo ? FormMode.EditMode : this.input.todoId ? FormMode.EditMode : FormMode.CreateMode,
      optimisticLock: true,
      children: {
        id: void 0,
        subject: { required: true },
        message: void 0,
        assignee: { required: true },
        dueDate: { required: true, type: DateTimeTypes.ShortDate, enabled: !this.input.activityTodo },
      },
    });
    if (this.mutateForm.editMode) {
      this.disableControlsOnEdit();
    }
  }

  disableControlsOnEdit(): void {
    this.mutateForm.disablePartial(['subject', 'message', 'assignee']);
  }

  enableControlsOnEdit(): void {
    this.mutateForm.enablePartial(['subject', 'message', 'assignee']);
  }

  loadMoreUsers($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    const fullQuery = { ...query, filter: { name: query.filter, showInactive: false } };
    load(this.todoListBusiness.getAutocompleteUser(fullQuery));
  }

  onSubmit(): void {
    if (this.mutateForm.group.invalid) {
      this.mutateForm.markAllAsTouched();
      return;
    }
    this.enableControlsOnEdit();
    const saveValue = fromForm(this.mutateForm.value);
    this.disableControlsOnEdit();

    const submitMessage = this.mutateForm.editMode ? 'msg_saved_successfully' : 'msg_created_successfully';
    const failure = (errors: any) => {
      if (this.mutateForm.editMode) {
        this.disableControlsOnEdit();
      }
      this.notificationService.processAll(errors);
    };

    const success = (responseTodo: TodoDetails) => {
      this.notificationService.success(submitMessage);
      this.dialogRef.close(responseTodo);
    };
    const saveAction$ = this.mutateForm.editMode
      ? this.todoListBusiness.update(saveValue)
      : this.todoListBusiness.create(saveValue);

    saveAction$.pipe(
      this.waitingService.operator<TodoDetails>())
      .subscribe({ next: success, error: failure });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  isControlInvalid(name: string): boolean {
    return !getControl(this.mutateForm.group, name).valid && getControl(this.mutateForm.group, name).dirty;
  }
}
